import { createSlice } from "@reduxjs/toolkit";
import {
  REQUEST_STATUS,
  type RequestStatus,
} from "@/modules/constants/REQUEST_STATUS";
import { createAppAsyncThunk } from "@/store/createAppAsyncThunk";
import type { RootState } from "@/store/store";
import { setCurrentEstateByIdThunk } from "../currentEstate/currentEstateSlice";
import { fetchEstateByIdAsync } from "../estateApi";

interface Estate {
  id: number;
  [key: string]: any;
}

interface EstateListState {
  items: Estate[];
  status: RequestStatus;
  error: string | null;
}

// Async thunk to fetch estates by an array of ids. The thunk will also set
// the first fetched estate as the current estate (if any) by dispatching the
// existing `setCurrentEstateByIdThunk`.
export const fetchEstatesByIds = createAppAsyncThunk(
  "estate/estateList/fetchEstatesByIds",
  async (estateIds: number[], thunkAPI) => {
    try {
      const promises = estateIds.map((id) => fetchEstateByIdAsync(id));
      const estates = (await Promise.all(promises)).map(
        (response) => response.data,
      );

      if (estates && estates.length > 0) {
        thunkAPI.dispatch(setCurrentEstateByIdThunk(estates[0].id));
      }

      return estates;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || "Failed to fetch estates");
    }
  },
);

const estatesSlice = createSlice({
  name: "estate/estateList",
  initialState: {
    items: [],
    status: REQUEST_STATUS.IDLE,
    error: null as string | null,
  } as EstateListState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEstatesByIds.pending, (state) => {
        state.status = REQUEST_STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchEstatesByIds.fulfilled, (state, action) => {
        state.items = action.payload;
        state.status = REQUEST_STATUS.SUCCEEDED;
      })
      .addCase(fetchEstatesByIds.rejected, (state, action) => {
        state.status = REQUEST_STATUS.FAILED;
        state.error =
          (action.payload as string) || action.error?.message || null;
      });
  },
});

export const reducer = estatesSlice.reducer;

// Provide a default export for the reducer for consumers expecting default import

export const selectEstateListStatus = (state: RootState) =>
  state.estates.estates.status;
export const selectEstateListItems = (state: RootState) =>
  state.estates.estates.items;
export const selectEstateById = (state: RootState, id: number) =>
  selectEstateListItems(state).find((estate) => estate.id === id);
